import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Copy,
  FileText,
  Lock,
  Pencil,
  Play,
  ShieldAlert,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { useGoBack } from "../../shared/hooks/goBack";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseDetail,
  ReleaseJourney,
  ReleaseReadiness,
  ReleaseTransitionView,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tabs,
  type TabDefinition,
} from "../../shared/ui";
import { ReadinessPanel } from "./ReadinessPanel";
import { ReleaseJourneyPanel } from "./ReleaseJourneyPanel";
import { ComponentReachCard } from "./ComponentReachCard";
import { ReleaseManifestCard } from "./ReleaseManifestCard";
import { CloneReleaseDrawer } from "./CloneReleaseDrawer";
import { ReleaseEditDrawer } from "./ReleaseEditDrawer";
import { ReleaseOverviewTab } from "./ReleaseOverviewTab";
import { ReleaseScopeTab } from "./ReleaseScopeTab";
import { ReleasePrerequisitesTab } from "./ReleasePrerequisitesTab";
import { ReleaseTestingTab } from "./ReleaseTestingTab";
import { ReleaseDateCard } from "./ReleaseDateCard";
import { ReleaseActivityTab } from "./ReleaseActivityTab";
import { ReleaseApprovalsTab } from "./ReleaseApprovalsTab";
import { ReleaseExecutionTab } from "./ReleaseExecutionTab";
import { ReleaseRollbackTab } from "./ReleaseRollbackTab";
import { ReleaseValidationTab } from "./ReleaseValidationTab";
import { ReleaseKnownIssuesTab } from "./ReleaseKnownIssuesTab";
import { ReleaseDocumentsTab } from "./ReleaseDocumentsTab";
import { ReleaseDeploymentsCard } from "./ReleaseDeploymentsCard";
import { ReleaseTransitionDrawer } from "./ReleaseTransitionDrawer";
import { FreezeScopeDrawer } from "./FreezeScopeDrawer";
import { GateOverrideDrawer } from "./GateOverrideDrawer";
import {
  READINESS_LABELS,
  READINESS_TONE,
  RELEASE_STATUS_LABELS,
  RELEASE_STATUS_TONE,
  RELEASE_TYPE_LABELS,
  RELEASE_TYPE_TONE,
  formatReleaseMoment,
} from "./releaseVocabulary";

type Tab =
  | "overview" | "scope" | "prerequisites" | "testing" | "approvals"
  | "execution" | "rollback" | "validation" | "documents" | "issues" | "activity";

/**
 * One release, and everything decided about it (§16, §17, §67).
 *
 * <p>Tabs rather than one long page, because §67 is right that putting every field on one screen
 * makes none of them findable. The header carries what somebody standing in a deployment window
 * needs without scrolling: what this is, when it goes, and whether it may.
 *
 * <p>The actions offered come from {@code GET /transitions} rather than from a list here. That is
 * the same decision the work-item board makes and for the same reason: a screen that keeps its own
 * copy of the workflow starts offering moves the server refuses the first time the rules change.
 */
export function ReleaseDetailPage() {
  const { releaseId = "" } = useParams();
  const navigate = useNavigate();
  const { goBack, hasHistory } = useGoBack("/release-calendar");
  const { can } = useAuth();
  const queryClient = useQueryClient();

  /*
   * Which leg of the journey is open, in the URL rather than in state (§62).
   *
   * <p>A release is not one screen. Somebody working a production window wants a link that
   * reopens exactly what they were looking at — not the release in general, from which they
   * would have to find their way back to production again. It also means the browser's back
   * button steps out of a leg, which is what pressing it there means.
   */
  const [params, setParams] = useSearchParams();
  const openLegId = params.get("env");

  const [tab, setTab] = useState<Tab>("overview");
  const [moving, setMoving] = useState<ReleaseTransitionView | null>(null);
  const [freezing, setFreezing] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [overriding, setOverriding] = useState(false);

  const release = useQuery({
    queryKey: ["release", releaseId],
    queryFn: () => api.get<ReleaseDetail>(`/api/v1/releases/${releaseId}`),
  });

  /*
   * Readiness for the leg being worked on, not for the release in the abstract. "Is 3.0 ready"
   * has no answer — it is ready for development and not for production, and a single verdict has
   * to pick one of those to be wrong about.
   */
  const readiness = useQuery({
    queryKey: ["release-readiness", releaseId, openLegId],
    queryFn: () =>
      api.get<ReleaseReadiness>(
        openLegId == null
          ? `/api/v1/releases/${releaseId}/readiness`
          : `/api/v1/releases/${releaseId}/readiness/${openLegId}`,
      ),
    enabled: release.data != null,
  });

  const journey = useQuery({
    queryKey: ["release-journey", releaseId],
    queryFn: () => api.get<ReleaseJourney>(`/api/v1/releases/${releaseId}/journey`),
    enabled: release.data != null,
  });

  /* Always re-asked when the page opens, and after any move. What can be done from here is the
     one thing that must never be answered from a cache the server has moved past. */
  const transitions = useQuery({
    queryKey: ["release-transitions", releaseId],
    queryFn: () =>
      api.get<ReleaseTransitionView[]>(`/api/v1/releases/${releaseId}/transitions`),
    enabled: release.data != null,
    staleTime: 0,
  });

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ["release", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-readiness", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-transitions", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-history", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-approvals", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-execution", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-rollback", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-validation", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-observation", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-known-issues", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-deployments", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-documents", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-journey", releaseId] });
    void queryClient.invalidateQueries({ queryKey: ["release-prerequisites", releaseId] });
  };

  const openLeg = (environmentId: string | null) => {
    const next = new URLSearchParams(params);
    if (environmentId == null) {
      next.delete("env");
    } else {
      next.set("env", environmentId);
    }
    setParams(next, { replace: false });
  };

  if (release.isPending) {
    return <LoadingState label="Loading release…" />;
  }
  if (release.isError) {
    return <ErrorState error={release.error} onRetry={() => void release.refetch()} />;
  }

  const data = release.data;

  /* The leg the URL names, resolved against what the server says the journey is. An id that is
     not on the journey — a stale link, a withdrawn environment — falls back to the journey view
     rather than showing a leg that is not there. */
  const activeLeg: EnvironmentView | null =
    (journey.data?.legs ?? []).find((row) => row.environment.id === openLegId)?.environment
    ?? null;
  const insideLeg = activeLeg != null;

  const tabs: TabDefinition<Tab>[] = [
    { key: "overview", label: "Overview" },
    { key: "scope", label: "Scope", badge: data.scope.total || undefined },
    { key: "prerequisites", label: "Pre-requisites" },
    { key: "testing", label: "Testing" },
    { key: "approvals", label: "Approvals" },
    { key: "execution", label: "Execution" },
    { key: "rollback", label: "Rollback" },
    { key: "validation", label: "Validation" },
    { key: "documents", label: "Documents" },
    { key: "issues", label: "Known issues" },
    { key: "activity", label: "Activity" },
  ];

  return (
    <>
      {/* The header §17 asks for — what, when, where, and whether it may go — with the what and
          the when in the bar along the top and the rest on one strip under it. */}
      <PageHeader
        title={data.releaseName}
        subtitle={formatReleaseMoment(data.plannedReleaseAt, data.timeZone)}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* Standing inside one environment's leg, back means leaving the leg — that is a step
            within this page, not a step out of it. Otherwise back is wherever the reader came
            from: the calendar, a report, the project. */}
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={() => (insideLeg ? openLeg(null) : goBack())}
        >
          {insideLeg
            ? `${data.releaseName} — all environments`
            : hasHistory
              ? "Back"
              : data.projectName}
        </Button>
        {/* The project stays one click away whichever way the button went. */}
        {!insideLeg && (
          <button
            type="button"
            onClick={() => navigate(`/projects/${data.projectId}`)}
            className="text-xs text-ink-500 hover:text-ink-800 hover:underline"
          >
            {data.projectName}
          </button>
        )}
        <span className="font-mono text-xs text-ink-500">{data.releaseKey}</span>
        <StatusBadge tone={RELEASE_TYPE_TONE[data.releaseType]}>
          {RELEASE_TYPE_LABELS[data.releaseType]}
        </StatusBadge>
        <StatusBadge tone={RELEASE_STATUS_TONE[data.status]}>
          {RELEASE_STATUS_LABELS[data.status]}
        </StatusBadge>
        {/* What this is a release of. Shown only when it is not the product, because
            "project release" on every release is a label carrying no information. */}
        {data.releaseScope !== "PROJECT" && data.module && (
          <StatusBadge tone={data.releaseScope === "COMPONENT" ? "violet" : "blue"}>
            {data.releaseScope === "COMPONENT" ? "Component" : "Module"} · {data.module.name}
          </StatusBadge>
        )}
        {activeLeg ? (
          /* Which leg you are standing in, said plainly. Everything below is scoped to it,
             and a screen that does not say so is how somebody ticks a production step
             thinking they are in the sandbox. */
          <StatusBadge tone={activeLeg.production ? "red" : "blue"}>
            Working on {activeLeg.name}
          </StatusBadge>
        ) : (
          data.targetEnvironment && (
            <StatusBadge tone={data.targetEnvironment.production ? "red" : "gray"}>
              Destination: {data.targetEnvironment.name}
            </StatusBadge>
          )
        )}
        {data.scopeFrozenAt && (
          <span
            className="inline-flex items-center gap-1 text-xs text-ink-500"
            title={`Frozen by ${data.scopeFrozenBy?.fullName ?? "somebody"}`}
          >
            <Lock className="h-3 w-3" aria-hidden />
            Scope frozen
          </span>
        )}
        <span className="ml-auto">
          <StatusBadge tone={READINESS_TONE[data.readinessStatus]}>
            {READINESS_LABELS[data.readinessStatus]}
          </StatusBadge>
        </span>
      </div>

      {/* A rollout cannot carry its source's scope — those work items belong to the other
          project — so it says where it came from. Without this the scope tab is empty with
          no explanation of what the release is actually carrying. */}
      {data.takenFrom && (
        <p className="mb-4 text-sm text-ink-600">
          Plan taken from{" "}
          <button
            type="button"
            className="font-medium text-brand-700 hover:underline"
            onClick={() => navigate(`/releases/${data.takenFrom?.id}`)}
          >
            {data.takenFrom.releaseName} ({data.takenFrom.version})
          </button>{" "}
          in {data.takenFrom.projectName} — its scope stays there.
        </p>
      )}

      {/* Actions, exactly as the server says they are. */}
      {can("RELEASE_EDIT") && (
        <div className="mb-5 flex flex-wrap gap-2">
          {!data.scopeFrozenAt && can("RELEASE_SCOPE_FREEZE") && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Lock className="h-4 w-4" />}
              onClick={() => setFreezing(true)}
            >
              Freeze scope
            </Button>
          )}
          {/* FREEZE_SCOPE has its own button above, which collects the reason and snapshot the
              generic transition drawer does not. Left in the list it appeared twice, the two
              copies doing subtly different things. */}
          {(transitions.data ?? [])
            .filter((transition) => transition.transition !== "FREEZE_SCOPE")
            .map((transition) => (
            <Button
              key={transition.transition}
              variant="secondary"
              size="sm"
              icon={
                transition.transition === "START_DEPLOYMENT" ? (
                  <Play className="h-4 w-4" />
                ) : undefined
              }
              title={transition.consequence}
              onClick={() => setMoving(transition)}
            >
              {transition.label}
            </Button>
            ))}
          <Button
            variant="ghost"
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            title="Owners, destination, description and build details"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
          {/* One artefact that says what shipped, who did each part, when, and what was known
              to be wrong — the eleven tabs are right during a release and wrong afterwards. */}
          <Button
            variant="ghost"
            size="sm"
            icon={<FileText className="h-4 w-4" />}
            title="The whole release as one printable document"
            onClick={() => navigate(`/releases/${releaseId}/report`)}
          >
            Release report
          </Button>
          {can("RELEASE_CREATE") && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Copy className="h-4 w-4" />}
              title="Start a new release from this one's prerequisites, approvals and runbook"
              onClick={() => setCloning(true)}
            >
              Start one like this
            </Button>
          )}
          {can("RELEASE_GATE_OVERRIDE")
            && (readiness.data?.blockingReasons.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ShieldAlert className="h-4 w-4" />}
              onClick={() => setOverriding(true)}
            >
              Accept a risk
            </Button>
          )}
        </div>
      )}

      {insideLeg && <Tabs tabs={tabs} active={tab} onChange={setTab} className="mb-4" />}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/*
            The landing view: the journey first, then the release summarised. Somebody opening a
            release wants to know where it is up to, and the answer to that is per-environment —
            not a merged list of every prerequisite anybody wrote for anywhere.
          */}
          {!insideLeg && (
            <>
              <ReleaseJourneyPanel release={data} onOpenLeg={openLeg} />
              <ReleaseManifestCard release={data} onChanged={reload} />
              <ComponentReachCard release={data} />
              <ReleaseOverviewTab release={data} onChanged={reload} />
              <ReleaseDeploymentsCard release={data} onChanged={reload} />
              <ReleaseActivityTab releaseId={data.id} />
            </>
          )}

          {insideLeg && tab === "overview" && (
            <ReleaseOverviewTab release={data} onChanged={reload} />
          )}
          {insideLeg && tab === "scope" && <ReleaseScopeTab release={data} onChanged={reload} />}
          {insideLeg && tab === "prerequisites" && (
            <ReleasePrerequisitesTab release={data} leg={activeLeg} onChanged={reload} />
          )}
          {insideLeg && tab === "testing" && <ReleaseTestingTab release={data} onChanged={reload} />}
          {insideLeg && tab === "approvals" && (
            <ReleaseApprovalsTab release={data} leg={activeLeg} onChanged={reload} />
          )}
          {insideLeg && tab === "execution" && (
            <div className="space-y-4">
              <ReleaseExecutionTab release={data} leg={activeLeg} onChanged={reload} />
              <ReleaseDeploymentsCard release={data} onChanged={reload} />
            </div>
          )}
          {insideLeg && tab === "rollback" && <ReleaseRollbackTab release={data} onChanged={reload} />}
          {insideLeg && tab === "validation" && <ReleaseValidationTab release={data} onChanged={reload} />}
          {insideLeg && tab === "documents" && <ReleaseDocumentsTab release={data} onChanged={reload} />}
          {insideLeg && tab === "issues" && <ReleaseKnownIssuesTab release={data} onChanged={reload} />}
          {insideLeg && tab === "activity" && <ReleaseActivityTab releaseId={data.id} />}
        </div>

        <div className="space-y-4">
          {/* Above readiness: the date is what people came to this page to find out. */}
          <ReleaseDateCard release={data} onChanged={reload} />

          {readiness.isPending ? (
            <Card>
              <CardBody>
                <LoadingState label="Checking the gates" />
              </CardBody>
            </Card>
          ) : readiness.data ? (
            <ReadinessPanel
              readiness={readiness.data}
              releaseId={releaseId}
              targetEnvironmentId={activeLeg?.id ?? data.targetEnvironment?.id ?? null}
              fixedToLeg={insideLeg}
              onChanged={reload}
            />
          ) : null}
        </div>
      </div>

      <ReleaseEditDrawer
        release={data}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={reload}
      />
      <CloneReleaseDrawer
        release={data}
        open={cloning}
        onClose={() => setCloning(false)}
      />
      <ReleaseTransitionDrawer
        release={data}
        transition={moving}
        onClose={() => setMoving(null)}
        onSaved={reload}
      />
      <FreezeScopeDrawer
        release={data}
        open={freezing}
        onClose={() => setFreezing(false)}
        onSaved={reload}
      />
      <GateOverrideDrawer
        release={data}
        readiness={readiness.data ?? null}
        open={overriding}
        onClose={() => setOverriding(false)}
        onSaved={reload}
      />
    </>
  );
}
